import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { OnboardingSuggestion } from '@/lib/types';

export function useOnboardingSuggestion(studentId: string) {
  const [suggestion, setSuggestion] = useState<OnboardingSuggestion | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSuggestion = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    setError(null);
    const { data, error: fetchErr } = await supabase
      .from('onboarding_suggestions')
      .select('*')
      .eq('student_id', studentId)
      .in('status', ['pending', 'accepted'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (fetchErr) {
      setError(fetchErr.message);
    } else {
      setSuggestion((data as OnboardingSuggestion) ?? null);
    }
    setLoading(false);
  }, [studentId]);

  useEffect(() => { fetchSuggestion(); }, [fetchSuggestion]);

  const markAccepted = useCallback(async (suggestionId: string) => {
    const { error: updateErr } = await supabase
      .from('onboarding_suggestions')
      .update({ status: 'accepted' })
      .eq('id', suggestionId);
    if (updateErr) { setError(updateErr.message); return; }
    await fetchSuggestion();
  }, [fetchSuggestion]);

  return { suggestion, loading, error, markAccepted, refetch: fetchSuggestion };
}
